import React, { createContext, useState, useContext, useEffect, Dispatch, SetStateAction } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { firebaseConfig, UserData } from "../util/FirebaseUtil";
import { linkPatreonToFirebaseUser, TierData } from "../util/PatreonUtil";
import { buildFileLibrary, EpisodeData, FileLibrary, SeriesData } from "./FileLibraryContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { sendAxiosPost } from "../util/ServerUtil";

type AppContextType = {
  isWaitingForData: boolean;
  isLoggedIn: boolean;
  patreonId: string | null;
  tierId: string;
  accessTier: number; // 0 = none or drifting, 1 = dreaming+
  isAdmin: boolean;
  fileLibrary: FileLibrary | null;
  SetPatreonAuthCallbackData: Dispatch<SetStateAction<PatreonAuthCallbackData | null>>;
};

const AppContext = createContext<AppContextType>({
  isWaitingForData: false,
  isLoggedIn: false,
  patreonId: null,
  tierId: "",
  accessTier: 0,
  isAdmin: false,
  fileLibrary: null,
  SetPatreonAuthCallbackData: () => null,
});

type AppContextProviderProps = {
  children: React.ReactNode;
};

type PatreonAuthCallbackData = {
  code: string;
  state: string;
};

initializeApp(firebaseConfig);

export const auth = getAuth();

let isSendingAuthRequest = false;

export function AppContextProvider({ children }: AppContextProviderProps): JSX.Element {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isWaitingForData, setIsWaitingForData] = useState(true);
  const [patreonId, setPatreonId] = useState<string | null>(null);
  const [tierId, setTierId] = useState("");
  const [accessTier, setAccessTier] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [fileLibrary, setFileLibrary] = useState<FileLibrary | null>(null);
  const [patreonAuthCallbackData, setPatreonAuthCallbackData] = useState<PatreonAuthCallbackData | null>(null);
  const [doneFirstAuthStateChange, setDoneFirstAuthStateChange] = useState(false);

  const getUserData = async (): Promise<UserData> => {
    const response = await sendAxiosPost("/api/getUserData", null, true);
    console.log("getUserData -> data:", response.data);
    return response.data || { patreonId: null, tierId: 0, fileLibrary: null, isAdmin: false };
  }

  useEffect(() => {
    if (!patreonAuthCallbackData) {
      return;
    }
    const code = patreonAuthCallbackData.code;
    const state = patreonAuthCallbackData.state;

    toast.info("Linking Patreon account...");
    linkPatreonToFirebaseUser(state, code).then((userData) => {
      console.log("linkPatreonToFirebaseUser -> userData:", userData);

      if (userData) {
        setAccessTier(TierData[userData.tierId]?.accessTier || 0);
        setIsAdmin(userData.isAdmin);
        setFileLibrary(buildFileLibrary(userData.fileLibrary));
        toast.success("Patreon account linked successfully!");
      }
      else {
        toast.error("Failed to link Patreon account.");
      }
      setPatreonAuthCallbackData(null); // Clear the callback data after processing
      window.location.href = "/profile"; // Redirect to profile page
    });
  }, [patreonAuthCallbackData]);

  // Persistent onAuthStateChanged listener
  onAuthStateChanged(auth, (user) => {
    if (!doneFirstAuthStateChange) {
      setDoneFirstAuthStateChange(true);
      setIsWaitingForData(false);
    }

    if (user?.uid && !isLoggedIn) {
      /* state changed, user is now logged in */
      setIsLoggedIn(true);
      onLoggedIn();
    } else if (!user?.uid && isLoggedIn) {
      /* state changed, user is now logged out */
      console.log("User logged out");
      setIsLoggedIn(false);
      // Reset user data
      setPatreonId(null);
      setTierId("");
      setAccessTier(0);
      setIsAdmin(false);
      setFileLibrary(null);
    }
  });

  const onLoggedIn = async () => {
    const user = auth.currentUser;
    if (user) {
      if (isSendingAuthRequest)
        return;
      isSendingAuthRequest = true;
      console.log("onLoggedIn -> User ID:", user.uid);
      setIsWaitingForData(true);
      await getUserData().then((data) => {
        setIsWaitingForData(false);
        // Set user data
        setPatreonId(data.patreonId);
        setTierId(data.tierId?.toString() || "");
        setAccessTier(TierData[data.tierId]?.accessTier || 0);
        setIsAdmin(data.isAdmin);
        setFileLibrary(buildFileLibrary(data.fileLibrary));
      });
      isSendingAuthRequest = false;
    }
  };

  return (
    <AppContext.Provider value={{ isWaitingForData, isLoggedIn, patreonId, tierId, accessTier, isAdmin, fileLibrary, SetPatreonAuthCallbackData: setPatreonAuthCallbackData }}>
      {children}
    </AppContext.Provider>
  );
}

/** 
 * Custom hook for easy usage of the context in child components
 */
export function useAppContext(): AppContextType {
  return useContext(AppContext);
}